import { useEffect, useRef, useState } from 'react'
import { formatNumber } from '@captaincause/lib/format'
import { tm } from '../../tm'
import { UrgentProjectBadge } from '../../UrgentProjectBadge'
import { ContributionsCount } from '../../ContributionsCount'
import { ProjectGemBadge } from '../../ProjectGemBadge'
import { DoneProject as DoneProjectBadge } from './Badge/DoneProject'
import { PointValue } from './Badge/PointValue'
import { AssociationName } from './AssociationName'
import { ProjectName } from './Badge/ProjectName'
import { DoneProjectMessage } from './Badge/DoneProjectMessage'
import type { Amount, Locales, Project } from '@captaincause/types'

interface CardImageProps {
  locale: Locales
  project: Project
  showPointAmount?: boolean
  showContributorsCount?: boolean
  showGemBadge?: boolean
  showProjectName?: boolean
  showBadges?: boolean
  hasPriorityImage?: boolean
  className?: string
  amount?: Amount
}

export function CardImage({
  locale,
  project,
  amount = undefined,
  showPointAmount = false,
  showContributorsCount = false,
  showGemBadge = false,
  showBadges = true,
  showProjectName = false,
  hasPriorityImage = false,
  className = '',
}: CardImageProps) {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (imgRef.current?.complete) {
      setLoaded(true)
    }
  }, [])

  let overlay = `linear-gradient(rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 70%, rgb(0, 0, 0) 100%)`
  if (project.is_done) {
    overlay = 'black'
  }
  if (showProjectName) {
    overlay = `linear-gradient(rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 100%)`
  }

  if (!project) return null

  return (
    <div
      className={tm(
        'relative mb-3 h-[336px] max-h-[336px] min-h-[336px] w-full flex-1 overflow-hidden rounded-2xl',
        className,
      )}
    >
      <img
        ref={imgRef}
        src={project.preview_image || project.image}
        alt={project.name}
        loading={hasPriorityImage ? 'eager' : 'lazy'}
        className="h-[inherit] w-full object-cover"
        onLoad={() => setLoaded(true)}
      />

      <div
        className={tm(
          'absolute inset-0 hidden h-full w-full opacity-50',
          loaded && 'block',
          project.is_done && 'opacity-40',
        )}
        style={{
          background: overlay,
        }}
      />
      <AssociationName
        project={project}
        className="absolute left-4 top-4 max-w-[90%]"
      />
      {showBadges ? (
        <>
          <UrgentProjectBadge
            project={project}
            locale={locale}
            className="absolute bottom-4 right-4"
          />

          <DoneProjectBadge
            project={project}
            locale={locale}
            className="absolute bottom-4 right-4"
          />

          <DoneProjectMessage
            project={project}
            locale={locale}
            className="absolute bottom-16 left-1/2 -translate-x-1/2"
          />
        </>
      ) : null}
      {showProjectName ? (
        <ProjectName project={project} className="absolute bottom-0 left-0" />
      ) : null}
      {showPointAmount && amount && project.action ? (
        <PointValue
          locale={locale}
          value={formatNumber(amount.amount, locale)}
          className="absolute bottom-4 left-4"
        />
      ) : null}
      {showContributorsCount ? (
        <ContributionsCount
          size={3}
          count={project.contributors_count}
          locale={locale}
          className="absolute bottom-4 left-4 pr-[120px] text-white"
        />
      ) : null}

      {showGemBadge ? (
        <ProjectGemBadge
          project={project}
          locale={locale}
          className="absolute right-4 top-4 hidden @xs:flex"
        />
      ) : null}
    </div>
  )
}
